import React from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  Button,
  Avatar,
  useMediaQuery,
  IconButton,
} from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import usePostPreviewTabsStyles from "./usePostPreviewTabsStyles";
import CloseIcon from "@material-ui/icons/Close";

interface PostPreviewModalProps {
  open: boolean;
  onClose: () => void;
  files: FileList | null;
  description: string;
  hashtags: string[];
  price: number | null;
  postType: string | null;
  currentUser: {
    firstName: string;
    lastName: string;
    username: string;
    profileImg?: string | null;
  } | null;
  onPublish?: () => void;
  isPublishing?: boolean;
}

const PostPreviewModal: React.FC<PostPreviewModalProps> = ({
  open,
  onClose,
  files,
  description,
  hashtags,
  price,
  postType,
  currentUser,
  onPublish,
  isPublishing,
}) => {
  const classes = usePostPreviewTabsStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const content = files
    ? Array.from(files).map((file) => ({
        mediaUrl: URL.createObjectURL(file),
        type: file.type.startsWith("image") ? "image" : "video",
      }))
    : [];

  const realUser = {
    firstName: currentUser?.firstName || "Unknown",
    lastName: currentUser?.lastName || "",
    username: `@${currentUser?.username || "unknown"}`,
    avatar: currentUser?.profileImg || "https://via.placeholder.com/100",
  };

  const post = {
    user: realUser,
    content,
    description,
    hashtags,
    price,
    postType,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      style={{ padding: "0 !important" }}
      classes={{ paper: classes.dialogPaper }}
    >
      {/* Хрестик для закриття */}
      <IconButton
        onClick={onClose}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          color: "#fff",
          zIndex: 10,
        }}
        aria-label="close"
      >
        <CloseIcon />
      </IconButton>
      <DialogContent classes={{ root: classes.dialogContentNoPadding }}>
        <Box
          className={classes.previewContainer}
          display="flex"
          flexDirection={isMobile ? "column" : "row"}
        >
          {/* Left side: media */}
          <Box className={classes.mediaColumn}>
            {content.length > 0 ? (
              content[0].type === "image" ? (
                <img
                  src={content[0].mediaUrl}
                  alt="Preview"
                  className={classes.previewMedia}
                />
              ) : (
                <video
                  src={content[0].mediaUrl}
                  controls
                  controlsList="nodownload nofullscreen noremoteplayback"
                  disablePictureInPicture
                  className={classes.previewMedia}
                />
              )
            ) : (
              <Box className={classes.emptyMedia}>
                <Typography style={{ color: "#777" }}>
                  No media selected
                </Typography>
              </Box>
            )}
          </Box>

          {/* Right side: user + text + hashtags + buttons */}
          <Box className={classes.infoColumn}>
            {/* User row */}
            <Box className={classes.userRow}>
              <Avatar
                src={realUser.avatar}
                alt={realUser.username}
                className={classes.userAvatar}
              />
              <Box>
                <Typography className={classes.userName}>
                  {realUser.firstName} {realUser.lastName}
                </Typography>
                <Typography className={classes.userHandle}>
                  {realUser.username}
                </Typography>
              </Box>
            </Box>

            {/* Caption (text only, no label) */}
            <Box className={classes.captionBox}>
              <Typography className={classes.captionText}>
                {description || "No caption added"}
              </Typography>
            </Box>

            {/* Hashtags as single text line */}
            {hashtags && hashtags.length > 0 && (
              <Box className={classes.hashtagsBox}>
                <Typography className={classes.hashtagsText}>
                  {hashtags.map(tag => `#${tag}`).join(" ")}
                </Typography>
              </Box>
            )}

            {/* Buttons */}
            <Box className={classes.buttonsRow}>
              <Button
                className={classes.backButton}
                onClick={onClose}
                fullWidth
              >
                Back to editor
              </Button>
              <Button
                className={classes.postButton}
                onClick={onPublish}
                disabled={!onPublish || !!isPublishing}
                fullWidth
              >
                {isPublishing ? "Publishing..." : "Post"}
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default PostPreviewModal;
